import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AcademyRichText } from '../academy/AcademyRichText';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import {
  DEMO_ASSESSMENT_PRESETS,
  demoAssessmentQuestions,
} from '@/pages/try/studyExperience/demoAssessments';
import {
  createStudyPracticeAttempt,
  STUDY_PRACTICE_OPTIONS,
  type StudyPracticeQuestion,
  type StudyPracticeOption,
} from './studyAnalysisFollowUpApi';
import { STUDY_HOME_QUERY_KEY } from './studyApi';
import { STUDY_REVIEW_QUERY_KEY } from './studyAnalysisFollowUpApi';
import {
  assessmentScore,
  selfAssessmentKey,
  selfAssessmentsKey,
  type SelfAssessmentPaper,
} from './studySelfAssessmentApi';

export function StudySelfAssessmentAnswers({
  paper,
  subjectKey,
  questionId,
}: {
  paper: SelfAssessmentPaper;
  subjectKey: string;
  questionId?: string;
}) {
  const demo = isLocalStudyExperience();
  const client = useQueryClient();
  const score = assessmentScore(paper.questions);
  const [answering, setAnswering] = useState(Boolean(questionId) || score.checked !== score.total);
  const form = useForm<{ answers: Record<string, string> }>({ defaultValues: { answers: {} } });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [checked, setChecked] = useState(0);
  const active = useRef(false),
    keys = useRef(new Map<string, string>()),
    sent = useRef(new Set<string>());
  const shown = questionId ? paper.questions.filter((q) => q.id === questionId) : paper.questions;
  const answers = form.watch('answers');
  const remainingWritten = shown.filter(
    (q) =>
      q.kind !== 'multiple_choice' &&
      !sent.current.has(JSON.stringify([q.id, answers[q.id]?.trim() ?? ''])),
  ).length;
  const owed = paper.questions.some(
    (q) =>
      q.latest_attempt?.status === 'failed' &&
      q.latest_attempt.stars > q.latest_attempt.stars_refunded,
  );
  const checkStars = paper.check_stars_per_attempt;
  const checkingUnavailable =
    remainingWritten > 0 && (paper.check_available === false || checkStars === null);
  const cost = remainingWritten * (checkStars ?? 0);
  const grade = async (values: { answers: Record<string, string> }) => {
    if (active.current) return;
    if (remainingWritten > 0 && checkStars === null) {
      setError('AI checking is unavailable. Refresh to check availability.');
      return;
    }
    const expectedStars = checkStars ?? 0;
    active.current = true;
    setBusy(true);
    setError('');
    try {
      for (const question of shown) {
        const answer = values.answers[question.id]?.trim() ?? '';
        const fingerprint = JSON.stringify([question.id, answer]);
        if (sent.current.has(fingerprint)) continue;
        const id = keys.current.get(fingerprint) ?? crypto.randomUUID();
        keys.current.set(fingerprint, id);
        const result = await createStudyPracticeAttempt(
          paper,
          question.id,
          question.kind === 'multiple_choice'
            ? { choice: answer as StudyPracticeOption, expected_stars: 0, idempotency_key: id }
            : {
                answer_text: answer,
                expected_stars: expectedStars,
                idempotency_key: id,
              },
        );
        client.setQueryData<SelfAssessmentPaper>(
          selfAssessmentKey(paper.analysis_id, paper.id),
          (current) =>
            current && {
              ...current,
              questions: current.questions.map((q) =>
                q.id === question.id ? { ...result.question, latest_attempt: result.attempt } : q,
              ),
            },
        );
        if (result.attempt.status !== 'checked') {
          if (result.attempt.refunded || result.attempt.stars === 0)
            keys.current.delete(fingerprint);
          throw new Error(
            result.attempt.refunded
              ? 'A check failed and its Stars were refunded. Retry the remaining answers.'
              : 'A check is not complete. Refresh the saved result before retrying; completed checks are kept.',
          );
        }
        sent.current.add(fingerprint);
        setChecked(sent.current.size);
      }
      setAnswering(false);
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'The remaining answers could not be checked.',
      );
    } finally {
      active.current = false;
      setBusy(false);
      void client.invalidateQueries({ queryKey: STUDY_REVIEW_QUERY_KEY });
      void client.invalidateQueries({ queryKey: STUDY_HOME_QUERY_KEY });
      void client.invalidateQueries({ queryKey: selfAssessmentsKey(subjectKey) });
    }
  };
  const tryAgain = () => {
    keys.current.clear();
    sent.current.clear();
    setChecked(0);
    setError('');
    form.reset({ answers: {} });
    setAnswering(true);
  };
  const useSample = () => {
    const preset = DEMO_ASSESSMENT_PRESETS.find(
      (p) => p.config.difficulty === paper.assessment.difficulty,
    );
    const samples = demoAssessmentQuestions(preset?.key ?? 'mixed');
    for (const q of shown) {
      const sample = samples.find((row) => row.stem === q.stem);
      if (sample)
        form.setValue(
          `answers.${q.id}`,
          q.kind === 'multiple_choice'
            ? sample.correct_option!
            : q.kind === 'working'
              ? sample.worked_solution
              : sample.answer,
          { shouldDirty: true },
        );
    }
  };
  if (!shown.length)
    return (
      <p role="alert" className="mt-5">
        This question is no longer in the assessment. Open the full assessment from the saved list.
      </p>
    );
  return (
    <section aria-label="Assessment answers and results" className="mt-5 space-y-4">
      {!answering && (
        <div className="rounded-3xl border border-hairline bg-wash-mint p-5">
          <h2 className="text-2xl font-extrabold">
            {score.correct} of {score.total} correct
          </h2>
          <p className="mt-2 text-sm">
            {score.checked} checked · {score.uncertain} uncertain or incomplete. This is feedback on
            these answers, not a school grade or proof of lasting understanding.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button className="btn-pill-secondary" onClick={tryAgain}>
              {questionId ? 'Try this question again' : 'Try assessment again'}
            </button>
            <Link className="btn-pill-secondary" to="/learn/study/review">
              Review wrong answers
            </Link>
          </div>
        </div>
      )}
      {answering && (
        <form
          aria-label="Answer this assessment"
          onSubmit={form.handleSubmit(grade)}
          className="space-y-4"
        >
          <h2 className="text-2xl font-extrabold">
            {questionId ? 'Review this question' : 'Answer the assessment'}
          </h2>
          <p className="text-sm">
            Previous answers and explanations stay hidden during this try. Complete every shown
            answer, then check them. For written questions, put the steps in these answer boxes.
            Separate My working pages are not submitted with this quiz.
          </p>
          <Link
            to="/learn/study/working"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block min-h-11 text-sm font-bold underline"
          >
            Open My working in a separate tab (optional)
          </Link>
          {demo && (
            <div className="rounded-xl bg-wash-sunshine p-3 text-sm">
              <p>
                Demo only: choices use a fixed key. Written responses replay the prepared sample;
                other text is marked uncertain, without AI judgement.
              </p>
              <button
                type="button"
                onClick={useSample}
                disabled={busy}
                className="btn-pill-secondary mt-2"
              >
                Use prepared sample answers
              </button>
            </div>
          )}
          <fieldset disabled={busy} className="space-y-4">
            {shown.map((q) => (
              <article
                key={q.id}
                id={`assessment-question-${q.id}`}
                className="scroll-mt-20 rounded-2xl border border-hairline bg-canvas-pure p-4"
              >
                <h3 className="mb-2 font-extrabold">
                  Question {paper.questions.indexOf(q) + 1} · {q.kind.replace('_', ' ')}
                </h3>
                <AcademyRichText text={q.stem} />
                {q.kind === 'multiple_choice' ? (
                  <fieldset className="mt-3 space-y-2">
                    <legend className="sr-only">
                      Answer question {paper.questions.indexOf(q) + 1}
                    </legend>
                    {q.options?.map((option, j) => (
                      <label
                        key={j}
                        className="flex min-h-11 items-center gap-2 rounded-xl bg-surface p-3 text-sm"
                      >
                        <input
                          type="radio"
                          value={STUDY_PRACTICE_OPTIONS[j]}
                          {...form.register(`answers.${q.id}`, { required: 'Choose an answer.' })}
                        />
                        <span className="font-bold">{STUDY_PRACTICE_OPTIONS[j]}</span>
                        <AcademyRichText text={option} />
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <label className="mt-3 block text-sm font-bold">
                    Your {q.kind === 'working' ? 'working and answer' : 'answer'}
                    <textarea
                      className="mt-1 min-h-24 w-full rounded-xl border border-hairline bg-canvas-pure p-3 text-sm font-normal"
                      rows={q.kind === 'working' ? 5 : 3}
                      maxLength={2000}
                      {...form.register(`answers.${q.id}`, { required: 'Write an answer.' })}
                    />
                  </label>
                )}
                {form.formState.errors.answers?.[q.id] && (
                  <p role="alert" className="mt-2 font-bold text-danger-600">
                    Complete this answer.
                  </p>
                )}
              </article>
            ))}
          </fieldset>
          {owed && (
            <p role="alert">
              A previous check has a refund pending. Refresh the result after settlement before
              another paid check.
            </p>
          )}
          {checkingUnavailable && (
            <p role="alert">
              AI checking is unavailable. Saved results remain readable. Refresh to check
              availability.
            </p>
          )}
          <p className="text-sm">
            {demo
              ? 'Demo checks use no Stars or live AI.'
              : `${cost} Stars for the remaining ${remainingWritten} written answers in this try; choice checks are free. Successful checks are saved separately. Retrying unchanged successful answers in this try does not charge them again.`}
          </p>
          <button
            className="btn-pill-primary"
            type="submit"
            disabled={busy || owed || checkingUnavailable}
          >
            {busy
              ? `Checking answers · ${checked}/${shown.length}`
              : error
                ? 'Retry remaining answers'
                : demo
                  ? 'Check demo answers'
                  : `Check all answers${cost ? ` · ${cost}★` : ''}`}
          </button>
          {error && (
            <p role="alert" className="rounded-xl bg-wash-coral p-3">
              {error} {checked} completed in this try. Your answers are kept.
            </p>
          )}
        </form>
      )}
      {!answering &&
        shown.map((q) => (
          <AssessmentQuestionResult
            key={q.id}
            question={q}
            number={paper.questions.indexOf(q) + 1}
            demo={demo}
          />
        ))}
      {questionId && (
        <p className="text-sm">
          This view reviews one question. Use the assessment title in the saved list to open the
          full result.
        </p>
      )}
    </section>
  );
}
function AssessmentQuestionResult({
  question: q,
  number,
  demo,
}: {
  question: StudyPracticeQuestion;
  number: number;
  demo: boolean;
}) {
  const latest = q.latest_attempt;
  const choiceIndex = STUDY_PRACTICE_OPTIONS.indexOf(
    latest?.student_answer_text as StudyPracticeOption,
  );
  const submitted =
    q.kind === 'multiple_choice' && choiceIndex >= 0 && q.options?.[choiceIndex]
      ? `${latest?.student_answer_text} · ${q.options[choiceIndex]}`
      : (latest?.student_answer_text ?? 'Not checked');
  const label =
    latest?.verdict === 'correct'
      ? 'Correct'
      : latest?.verdict === 'incorrect'
        ? 'Needs correction'
        : latest?.verdict === 'incomplete'
          ? 'Incomplete'
          : 'Uncertain · needs review';
  return (
    <article
      aria-label={`Question ${number} result`}
      id={`assessment-question-${q.id}`}
      className="rounded-2xl border border-hairline bg-canvas-pure p-5"
    >
      <h3 className="mb-2 font-extrabold">
        Question {number} · {label}
      </h3>
      <AcademyRichText text={q.stem} />
      <div className="mt-3 rounded-xl bg-surface p-3 text-sm">
        <p className="font-bold">
          {demo && latest?.id.startsWith('prepared-')
            ? 'Prepared student answer'
            : 'Submitted answer'}
        </p>
        <AcademyRichText text={submitted} />
        {latest?.hint && <p className="mt-2">{latest.hint}</p>}
        <p className="mt-2 text-xs text-slate2">
          {demo
            ? 'Pre-generated Demo feedback or fixed-key comparison; no live AI.'
            : q.kind === 'multiple_choice'
              ? 'Checked against the saved option key.'
              : 'Checked by AI · may need human review.'}
        </p>
      </div>
      {q.answer && (
        <div className="mt-3 rounded-xl bg-wash-mint p-3">
          <h4 className="font-extrabold">Reference answer</h4>
          <AcademyRichText
            text={`${q.answer.correct_option ? `${q.answer.correct_option} · ` : ''}${q.answer.answer}`}
          />
          <h4 className="mt-3 font-extrabold">Step-by-step explanation</h4>
          <div className="mt-1 whitespace-pre-wrap text-sm">
            <AcademyRichText text={q.answer.worked_solution} />
          </div>
        </div>
      )}
    </article>
  );
}
